// Moments that used to pass with no burst of their own: a summoned creature arriving or leaving, Связка cinching on
// a hero, a point taken, and the birth of the lasting things fx.ts zones() draws (Павеза, a trap, a lantern, a holy
// circle, Прорицание, a cloud, a fire or tar pool). Same ink as fxSkills.ts — strokes, blots, hatched bits, no glow.
// The births have no event: fx.ts calls `births` once per entity, only while its own clock says it was just made.
import * as THREE from 'three';
import { flat, TEAM } from './engrave.js';
import { LAYOUT, type Team } from '../core/layout.js';
import type { SummonKind } from '../core/summons.js';
import type { ViewBarrier, ViewHero, ViewPool, ViewReveal, ViewTrap } from './gameView.js';
import { FACE, strokeGeometry } from './fxImpacts.js';
import { featherGeometry, inkTools, type SkillKit } from './fxSkills.js';

export type MomentKit = Pick<SkillKit, 'spawn' | 'puff' | 'fxMesh' | 'ink'> & {
  /** Who holds the point at (or right by) this spot now, null when nobody or no point is there. */
  ownerAt: (pos: Spot) => Team | null;
  /** Seconds Связка's thrown coil is drawn in the air: the cord cinches once it lands. */
  cordDelay: number;
};
type Spot = { x: number; z: number };

const TAU = Math.PI * 2;
const DUST = 0xcbbf9f, EARTH = 0x6a5440, STONE = 0x8a8276, CORD = 0xb07a5a, STEEL = 0xe0d8bc, EMBER = 0xb8482a,
  MAGPIE = 0x26262a, MAGPIE_WHITE = 0xe8e4d8, WOLF_GREY = 0x5a5560, LANTERN = 0x9a86c8, LANTERN_PALE = 0xc8b8e8,
  MOTE = 0xf6e8a0, GOLD = 0xf2d46b, STAR = 0xd8e0ff, PLAGUE = 0x6f7a50, FLAME = 0xd8642a, SMOKE = 0x6a6058;
const MAGPIE_Y = 2.1;  // summonView.ts's magpie body height
const UP = new THREE.Vector3(0, 1, 0);

const rnd = (a: number, b: number) => a + Math.random() * (b - a);
/** `k` re-timed to start after `delay` (both fractions of a life): ≤ 0 until then. */
const after = (k: number, delay: number) => (k - delay) / (1 - delay);

/* A moth: two pale wings hinged on a short body, flapped by rotation.y of each. */
function mothGeometry() {
  const s = new THREE.Shape();
  s.moveTo(0, 0); s.quadraticCurveTo(0.22, 0.16, 0.2, -0.04); s.quadraticCurveTo(0.12, -0.1, 0, 0);
  return new THREE.ShapeGeometry(s);
}

export function createMomentFx({ spawn, puff, fxMesh, ink, ownerAt, cordDelay }: MomentKit) {
  const V = new THREE.Vector3(), P = new THREE.Vector3();
  const { bit, anyBlot, toss, sink, groundStroke, crack, blot } = inkTools({ spawn, fxMesh });

  /* Feathers let go at the magpie's height: black and white, thrown out by `speed`, sinking with a sway. */
  function feathers(pos: Spot, n: number, speed: number, dur: number) {
    for (let i = 0; i < n; i++) {
      const a = i / n * TAU + rnd(-0.3, 0.3), c = i % 2 ? MAGPIE : MAGPIE_WHITE, sp = speed * rnd(0.7, 1.2);
      sink('m:feather' + c, () => bit(featherGeometry(), c, true), P.set(pos.x, MAGPIE_Y + rnd(-0.2, 0.2), pos.z),
        V.set(Math.cos(a) * sp, rnd(0.5, 2), Math.sin(a) * sp), dur, 1.2, 1.1);
    }
  }
  /* Ink welling up from the ground and drawing back in (`gather`), or splashed down and melting into it. */
  function inkPool(pos: Spot, size: number, dur: number, gather: boolean) {
    const geo = anyBlot();
    spawn('m:pool' + geo.uuid, () => flat(fxMesh(geo, ink), 0.235), dur, (it) => {
      it.obj.position.set(pos.x, 0.235, pos.z); it.r0 = Math.random() * TAU; it.s = size; it.gather = gather;
    }, (it, k) => {
      const grow = it.gather ? 1 - Math.pow(1 - Math.min(1, k / 0.4), 3) : Math.min(1, k * 10);
      const out = it.gather ? (k > 0.55 ? 1 - (k - 0.55) / 0.45 : 1) : 1 - k * k;
      it.obj.scale.set(it.s * grow * out + 0.001, it.s * grow * out * (it.gather ? 1 : 1 - k * 0.3) + 0.001, 1);
      it.obj.rotation.z = it.r0 + (it.gather ? k * 1.4 : 0);
    });
  }
  /* Brush strokes standing up off a spot: in toward it and up (`gather`: a creature drawn together), or out and down. */
  function wisps(pos: Spot, n: number, dur: number, gather: boolean) {
    for (let i = 0; i < n; i++) {
      const c = i % 2 ? ink : WOLF_GREY;
      spawn('m:wisp' + c, () => fxMesh(strokeGeometry(0.55, 0.13, 0.12).translate(-0.27, 0, 0), c, { double: true }), dur, (it) => {
        it.p = it.p || new THREE.Vector3(); it.p.set(pos.x, 0, pos.z);
        it.a = i / n * TAU + rnd(-0.2, 0.2); it.dl = rnd(0, 0.25); it.gather = gather;
      }, (it, k) => {
        const u = after(k, it.dl), o = it.obj;
        if (u <= 0) { o.scale.set(0.001, 0.001, 1); return; }
        const r = it.gather ? 1.0 - u * 0.8 : 0.4 + u * 0.9, y = it.gather ? 0.3 + u * 0.8 : 0.9 - u * 0.6;
        o.position.set(it.p.x + Math.cos(it.a) * r, y, it.p.z + Math.sin(it.a) * r);
        o.rotation.set(FACE, 0, Math.PI / 2 + Math.cos(it.a) * 0.5);  // upright in the picture, leaning with its side
        const g = Math.min(1, u * 5) * (1 - u);
        o.scale.set(g + 0.001, g + 0.001, 1);
      });
    }
  }
  /* Earth thrown up from a spot: clods flung out, dust rolling off along the ground. */
  function earth(pos: Spot, clods: number, dust: number, s: number, clod = EARTH) {
    for (let i = 0; i < clods; i++) {
      const a = Math.random() * TAU, sp = rnd(1.2, 3) * s;
      toss('m:clod' + clod, () => bit(new THREE.DodecahedronGeometry(0.15, 0), clod), P.set(pos.x + Math.cos(a) * 0.4, 0.35, pos.z + Math.sin(a) * 0.4),
        V.set(Math.cos(a) * sp, rnd(3.5, 6), Math.sin(a) * sp), 0.85, 14, rnd(0.8, 1.4));
    }
    for (let i = 0; i < dust; i++) {
      const a = i / dust * TAU + rnd(-0.3, 0.3);
      puff(P.set(pos.x + Math.cos(a) * 0.6, 0.3, pos.z + Math.sin(a) * 0.6), DUST,
        { size: 1.1 * s, rise: 0.4, dur: 0.75, drift: V.set(Math.cos(a) * 1.8 * s, 0, Math.sin(a) * 1.8 * s) });
    }
  }
  /* A four-point glint at a spot, popping and turning. */
  function glint(x: number, y: number, z: number, size: number, dur: number) {
    for (const a of [0, Math.PI / 2]) {
      spawn('m:glint', () => bit(strokeGeometry(0.8, 0.13, 0).translate(-0.4, 0, 0), STEEL, true), dur, (it) => {
        it.obj.position.set(x, y, z); it.a = a; it.s = size;
      }, (it, k) => {
        const g = k < 0.3 ? k / 0.3 : 1 - (k - 0.3) / 0.7;
        it.obj.rotation.set(FACE, 0, it.a + k * 0.8);
        it.obj.scale.setScalar(g * it.s + 0.001);
      });
    }
  }
  /* Motes rising from spots `at(i)` with a sway, each a little after the one before, shrinking out. */
  function motes(n: number, color: number, at: (i: number, out: THREE.Vector3) => void, rise: number, dur: number, size = 0.1) {
    for (let i = 0; i < n; i++) {
      spawn('m:mote' + color + size, () => fxMesh(new THREE.OctahedronGeometry(size), color), dur, (it) => {
        at(i, it.p ||= new THREE.Vector3()); it.dl = rnd(0, 0.3); it.ph = Math.random() * TAU; it.rise = rise;
      }, (it, k) => {
        const u = after(k, it.dl), o = it.obj;
        if (u <= 0) { o.scale.setScalar(0.001); return; }
        o.position.set(it.p.x + Math.sin(it.ph + u * 8) * 0.15, it.p.y + u * it.rise, it.p.z + Math.cos(it.ph + u * 7) * 0.15);
        o.rotation.y = u * 6;
        o.scale.setScalar(Math.min(1, u * 6) * (1 - u) * 1.4 + 0.001);
      });
    }
  }

  const events: Record<string, (e: any) => void> = {
    /* A summoned creature arriving: magpies in a burst of feathers, wolves drawn up out of an ink pool, the golem
       heaving up out of the earth. */
    summon({ pos, kind }: { pos: Spot; kind: SummonKind }) {
      if (kind === 'magpie') {
        feathers(pos, 8, 6, 1.1);
        puff(P.set(pos.x, MAGPIE_Y, pos.z), 0xd8d0bc, { size: 0.9, rise: 0.3, dur: 0.5 });
      } else if (kind === 'wolf') {
        inkPool(pos, 1.3, 0.9, true);
        wisps(pos, 8, 0.8, true);
      } else if (kind === 'golem') {
        earth(pos, 9, 6, 1.1);
        const a0 = Math.random() * TAU;
        for (let i = 0; i < 3; i++) crack(pos, a0 + i / 3 * TAU + rnd(-0.4, 0.4), 0.7, [rnd(0.6, 0.9), rnd(0.4, 0.6)], 0.9, ink, 0.12);
      }
    },
    /* …and leaving: feathers scattering, ink splashed down and melting into the ground, the golem's heap raising
       dust (summonView.ts crumbles the figure itself). Killed reads harder than run out. */
    summonGone({ pos, kind, destroyed }: { pos: Spot; kind: SummonKind; destroyed?: boolean }) {
      if (kind === 'magpie') {
        feathers(pos, destroyed ? 10 : 5, destroyed ? 8 : 4, destroyed ? 1.0 : 1.3);
      } else if (kind === 'wolf') {
        inkPool(pos, destroyed ? 1.3 : 0.9, destroyed ? 1.2 : 1.0, false);
        wisps(pos, destroyed ? 4 : 6, 0.7, false);
        if (destroyed) for (let i = 0; i < 6; i++) {
          const a = i / 6 * TAU + rnd(-0.3, 0.3), sp = rnd(2, 3.5);
          toss('m:drop', () => fxMesh(new THREE.SphereGeometry(0.08, 8, 6), ink), P.set(pos.x, 0.7, pos.z), V.set(Math.cos(a) * sp, rnd(2, 3.5), Math.sin(a) * sp), 0.6, 12);
        }
      } else if (kind === 'golem') {
        earth(pos, destroyed ? 6 : 0, 7, destroyed ? 1.2 : 0.9, STONE);
      }
    },
    /* Связка tied this hero: once the coil lands, three loops of cord cinch in round the waist, then let go. */
    tether({ hero }: { hero?: ViewHero }) {
      if (!hero) return;
      const dur = cordDelay + 0.75, dl = cordDelay / dur;
      for (let i = 0; i < 3; i++) {
        spawn('m:loop', () => fxMesh(new THREE.TorusGeometry(1, 0.075, 6, 28), CORD), dur, (it) => {
          it.h = hero; it.y = 0.75 + i * 0.22; it.tilt = (i - 1) * 0.18; it.dl = dl + i * 0.04;
        }, (it, k) => {
          const u = after(k, it.dl), o = it.obj, h = it.h;
          if (u <= 0) { o.scale.setScalar(0.001); return; }
          const cinch = 1 - Math.pow(1 - Math.min(1, u / 0.35), 3), off = u > 0.75 ? (u - 0.75) / 0.25 : 0;
          o.position.set(h.pos.x, it.y + h.lift, h.pos.z);
          o.rotation.set(Math.PI / 2 + it.tilt, 0, u * 2);
          o.scale.setScalar((1.5 - 0.95 * cinch) * (1 - off * 0.4) + 0.001);
          o.visible = off < 1;
        });
      }
      // the knot pulled tight in front
      spawn('m:knot', () => fxMesh(new THREE.IcosahedronGeometry(0.11, 0), CORD), dur, (it) => { it.h = hero; it.dl = dl + 0.3 * (1 - dl); }, (it, k) => {
        const u = after(k, it.dl), h = it.h;
        it.obj.position.set(h.pos.x, 0.97 + h.lift, h.pos.z + 0.58);
        it.obj.scale.setScalar(u <= 0 ? 0.001 : (u < 0.2 ? u / 0.2 * 1.4 : 1.4 - (u - 0.2) * 0.6) + 0.001);
      });
    },
    /* A point taken: ink strokes run out along the ground from its rim, motes in the new owner's colour rise off it. */
    capture({ pos }: { pos: Spot }) {
      const owner = ownerAt(pos), color = owner ? TEAM[owner] : ink, R = LAYOUT.pointRadius;
      for (let i = 0; i < 14; i++) {
        const a = i / 14 * TAU + rnd(-0.1, 0.1);
        groundStroke(ink, rnd(1.6, 2.6), 0.14, pos.x + Math.cos(a) * R * 0.9, pos.z + Math.sin(a) * R * 0.9, a, 1.1, (i % 2) * 0.12);
      }
      motes(16, color, (_, out) => {
        const a = Math.random() * TAU, d = Math.sqrt(Math.random()) * R;
        out.set(pos.x + Math.cos(a) * d, 0.3, pos.z + Math.sin(a) * d);
      }, 2.6, 1.3, 0.13);
    },
  };

  const pool: Record<string, (z: ViewPool) => void> = {
    /* Фонарь Перевозчика lit: violet strokes flare out of the lamp, a few moths flutter off it. */
    lantern(z) {
      const y = 1.7;
      for (let i = 0; i < 10; i++) {
        const c = i % 2 ? LANTERN : LANTERN_PALE, a = i / 10 * TAU + rnd(-0.15, 0.15);
        spawn('m:flare' + c, () => fxMesh(strokeGeometry(0.8, 0.12, 0), c, { double: true }), 0.5, (it) => {
          it.a = a; it.x0 = z.pos.x; it.z0 = z.pos.z; it.dl = (i % 3) * 0.08;
        }, (it, k) => {
          const u = after(k, it.dl), o = it.obj;
          if (u <= 0) { o.scale.set(0.001, 0.001, 1); return; }
          o.position.set(it.x0, y, it.z0); o.rotation.set(FACE, 0, it.a);
          o.translateX(0.3 + u * 1.3);  // out along its own direction in the picture
          o.scale.set(Math.min(1, u * 4) * (1 - u * 0.5) + 0.001, 1 - u + 0.001, 1);
        });
      }
      for (let i = 0; i < 3; i++) {
        spawn('m:moth', () => {
          const g = new THREE.Group();
          for (const side of [-1, 1]) {
            const w = fxMesh(mothGeometry(), LANTERN_PALE, { double: true });
            w.scale.x = side; w.userData.side = side;
            g.add(w);
          }
          return g;
        }, 1.3, (it) => {
          (it.p ||= new THREE.Vector3()).set(z.pos.x, y, z.pos.z); it.a = i / 3 * TAU + rnd(-0.4, 0.4); it.ph = Math.random() * TAU;
        }, (it, k) => {
          const o = it.obj, a = it.a + Math.sin(it.ph + k * 6) * 0.6;
          o.position.set(it.p.x + Math.cos(a) * k * 2.2, it.p.y + k * 1.2 + Math.sin(it.ph + k * 20) * 0.1, it.p.z + Math.sin(a) * k * 2.2);
          o.rotation.set(FACE, 0, -a);
          for (const w of o.children) w.rotation.y = w.userData.side * Math.abs(Math.sin(it.ph + k * 45)) * 1.1;
          o.scale.setScalar(1.6 * Math.min(1, k * 8) * (k > 0.7 ? 1 - (k - 0.7) / 0.3 : 1) + 0.001);
        });
      }
    },
    /* Святой круг cast: gold motes rising off its rim (the pillars stand there after). */
    holy(z) {
      motes(18, MOTE, (i, out) => {
        const a = i / 18 * TAU;
        out.set(z.pos.x + Math.cos(a) * z.r, 0.3, z.pos.z + Math.sin(a) * z.r);
      }, 2.4, 1.1, 0.11);
      motes(6, GOLD, (i, out) => {
        const a = (i + 0.5) / 6 * TAU;
        out.set(z.pos.x + Math.cos(a) * z.r, 0.3, z.pos.z + Math.sin(a) * z.r);
      }, 3.2, 0.9, 0.15);
    },
    /* Чумное облако set loose: its smoke heaves out in one whoosh. */
    cloud(z) {
      for (let i = 0; i < 8; i++) {
        const a = i / 8 * TAU + rnd(-0.2, 0.2);
        puff(P.set(z.pos.x + Math.cos(a) * 0.4, 0.6, z.pos.z + Math.sin(a) * 0.4), PLAGUE,
          { size: 1.5, rise: 0.5, dur: 0.7, drift: V.set(Math.cos(a) * z.r * 1.2, 0, Math.sin(a) * z.r * 1.2) });
      }
      for (let i = 0; i < 3; i++) puff(P.set(z.pos.x + rnd(-0.4, 0.4), 0.8, z.pos.z + rnd(-0.4, 0.4)), 0x8a9460, { size: 1.6, rise: 1.2, dur: 0.8 });
    },
    /* Огненная лужа lit: tongues of flame leap up, embers fly, dark smoke rolls off. */
    fire(z) {
      for (let i = 0; i < 5; i++) {
        const a = i / 5 * TAU + rnd(-0.3, 0.3), d = rnd(0.2, z.r * 0.6);
        spawn('m:tongue', () => bit(new THREE.ConeGeometry(0.2, 1.4, 5).translate(0, 0.7, 0), FLAME), 0.55, (it) => {
          it.obj.position.set(z.pos.x + Math.cos(a) * d, 0.2, z.pos.z + Math.sin(a) * d); it.dl = rnd(0, 0.2); it.h = rnd(0.8, 1.4);
        }, (it, k) => {
          const u = after(k, it.dl), up = u <= 0 ? 0 : u < 0.3 ? u / 0.3 : 1 - (u - 0.3) / 0.7;
          it.obj.scale.set(1 - Math.max(0, u) * 0.5 + 0.001, up * it.h + 0.001, 1 - Math.max(0, u) * 0.5 + 0.001);
        });
      }
      for (let i = 0; i < 8; i++) {
        const a = Math.random() * TAU, sp = rnd(0.8, 2.2);
        toss('m:ember', () => bit(new THREE.OctahedronGeometry(0.09), EMBER), P.set(z.pos.x + Math.cos(a) * 0.4, 0.4, z.pos.z + Math.sin(a) * 0.4),
          V.set(Math.cos(a) * sp, rnd(3.5, 5.5), Math.sin(a) * sp), 0.8, 7, rnd(0.8, 1.3), 6);
      }
      for (let i = 0; i < 3; i++) puff(P.set(z.pos.x + rnd(-0.6, 0.6), 0.5, z.pos.z + rnd(-0.6, 0.6)), SMOKE, { size: 1.3, rise: 1.3, dur: 0.9 });
    },
    /* Смоляная бутыль broken: tar splashed out, drops landing round the pool. */
    tar(z) {
      for (let i = 0; i < 9; i++) {
        const a = i / 9 * TAU + rnd(-0.3, 0.3), sp = rnd(1.8, 3.2);
        toss('m:tar', () => fxMesh(new THREE.SphereGeometry(0.1, 8, 6), ink), P.set(z.pos.x, 0.5, z.pos.z), V.set(Math.cos(a) * sp, rnd(2.5, 4), Math.sin(a) * sp), 0.6, 12);
      }
      for (let i = 0; i < 5; i++) {
        const a = i / 5 * TAU + rnd(-0.4, 0.4), d = z.r * rnd(0.9, 1.2);
        blot(ink, z.pos.x + Math.cos(a) * d, z.pos.z + Math.sin(a) * d, rnd(0.25, 0.4), 1.4, 0.235);
      }
    },
  };

  const births = {
    /** Whether `look` has a birth burst — fx.ts only latches the pools that do. */
    hasPool: (look: string | undefined) => !!look && Object.hasOwn(pool, look),
    pool(z: ViewPool) { if (z.look && Object.hasOwn(pool, z.look)) pool[z.look]!(z); },
    /* Павеза landing: dust thrown out along its base on both sides, a line of ink under it. */
    barrier(b: ViewBarrier) {
      const len = Math.hypot(b.dir.x, b.dir.z) || 1, fx = b.dir.x / len, fz = b.dir.z / len, tx = fz, tz = -fx;  // tx,tz: along the board
      const n = Math.max(4, Math.round(b.half * 2.5));
      for (let i = 0; i < n; i++) {
        const s = (i / (n - 1) * 2 - 1) * b.half, side = i % 2 ? 1 : -1;
        puff(P.set(b.center.x + tx * s, 0.25, b.center.z + tz * s), DUST,
          { size: 1.0, rise: 0.35, dur: 0.7, drift: V.set(fx * side * 1.6, 0, fz * side * 1.6) });
      }
      const ang = Math.atan2(tz, tx);
      for (const side of [-1, 1]) {
        groundStroke(ink, b.half * 2, 0.1, b.center.x - tx * b.half + fx * side * 0.25, b.center.z - tz * b.half + fz * side * 0.25, ang, 0.6);
      }
      for (let i = 0; i < 4; i++) {
        const s = rnd(-b.half, b.half), side = i % 2 ? 1 : -1, sp = rnd(1, 2);
        toss('m:clod' + EARTH, () => bit(new THREE.DodecahedronGeometry(0.15, 0), EARTH), P.set(b.center.x + tx * s, 0.3, b.center.z + tz * s),
          V.set(fx * side * sp, rnd(2.5, 4), fz * side * sp), 0.6, 14, rnd(0.6, 1));
      }
    },
    /* Ловушка Аркебузира set: a little dust and a glint off the vial. */
    trap(t: ViewTrap) {
      for (let i = 0; i < 4; i++) {
        const a = i / 4 * TAU;
        puff(P.set(t.pos.x, 0.25, t.pos.z), DUST, { size: 0.8, rise: 0.3, dur: 0.6, drift: V.set(Math.cos(a) * 1.2, 0, Math.sin(a) * 1.2) });
      }
      glint(t.pos.x, 0.8, t.pos.z + 0.3, 1.5, 0.6);
    },
    /* Прорицание: a few stars falling slantwise onto the spot, each flaring as it lands. */
    reveal(r: ViewReveal) {
      for (let i = 0; i < 5; i++) {
        const a = Math.random() * TAU, d = Math.sqrt(Math.random()) * r.r * 0.5, lean = Math.random() * TAU;
        spawn('m:star', () => {
          const g = new THREE.Group();
          const star = fxMesh(new THREE.OctahedronGeometry(0.22), STAR);
          star.scale.set(1, 1.3, 1);
          const tail = fxMesh(new THREE.CylinderGeometry(0.035, 0.005, 1.8, 5).translate(0, 0.9, 0), STAR);
          g.add(star, tail);
          return g;
        }, 0.8, (it) => {
          (it.to ||= new THREE.Vector3()).set(r.x + Math.cos(a) * d, 0.35, r.z + Math.sin(a) * d);
          (it.from ||= new THREE.Vector3()).set(it.to.x + Math.cos(lean) * 2.5, 7, it.to.z + Math.sin(lean) * 2.5);
          it.obj.quaternion.setFromUnitVectors(UP, V.subVectors(it.from, it.to).normalize());  // the tail trails back up the path
          it.dl = i * 0.08; it.fall = 0.45;
        }, (it, k) => {
          const u = after(k, it.dl), o = it.obj;
          if (u <= 0) { o.scale.setScalar(0.001); return; }
          const f = Math.min(1, u / it.fall), land = u > it.fall ? (u - it.fall) / (1 - it.fall) : 0;
          o.position.lerpVectors(it.from, it.to, f * f);
          o.children[1]!.scale.set(1, (1 - land) + 0.001, 1);
          o.children[0]!.scale.set(1, 1.3, 1).multiplyScalar(land ? (land < 0.25 ? 1 + land * 3 : 1.75 * (1 - land) / 0.75) + 0.001 : 1);
          o.scale.setScalar(1);
        });
      }
    },
  };

  return { events, births };
}
